import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { makeStyles } from '@mui/styles'
import React, { useEffect, useMemo, useRef } from 'react'
import clsx from 'clsx'

import LogoSvg from '@assets/images/logo/svg/logo_light.svg'
import { fetchMenuItems } from '@/redux/ducks/apps'
import ThemeInterface from '@/theme-interface'
import NotificationButton from '@/modules/base/components/notification/button/notification-button'
import { getAppsMenuItem, getMarketplaceMenuItem, getSettingsMenuItem } from '@/modules/layout/components/menu/config'
import AccountButton from '@/modules/layout/components/menu/header/account-button'
import MenuBarItem from '@/modules/layout/components/menuV2/menu-bar/menu-bar-item'
import UpgradeNotch from '@/modules/layout/components/menuV2/top-bar/upgrade-notch'
import { checkAccessRights } from '@/modules'
import { USER_RIGHTS, SUBSCRIPTION_MODULES, subscriptionTypes, SubscriptionTypeTypes, MenuItems } from '@/modules/base/util/constants'
import TaxConsultantUserSwitchNotch from '@/modules/layout/components/menuV2/top-bar/taxconsultant-user-switch-notch'
import { isMeinBuero } from '@/utils/build-env'
import CelloButton from '@/modules/cello/cello-button'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import UploadStatusButton from '@/modules/layout/components/menuV2/upload-status/upload-status'
import { useNavigate } from 'react-router'
import StopDemoNotch from '@/modules/layout/components/menuV2/top-bar/stop-demo-notch'
import RevenueLimitNotch from '@/modules/layout/components/menuV2/top-bar/revenue-limit-notch'
import { isOnboardingIconEnabled } from '@/modules/_helpers/firstSteps'

const isMeinbuero = isMeinBuero()

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'grid',
    gridTemplateAreas: '"left center right"',
    gridTemplateColumns: '1fr 1fr 1fr',
    background: theme.navTopBarBackgroundColor,
    padding: '0px 30px',
    maxWidth: '100vw',
  },
  leftArea: {
    gridArea: 'left',
  },
  centerArea: {
    gridArea: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  rightArea: {
    gridArea: 'right',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    '& > div:not(:last-child), a': {
      margin: '0px 10px',
    },
    '& > div:last-child': {
      marginLeft: 20,
      marginRight: 0,
    },
  },
  logoWrapper: {
    display: 'flex',
    height: '100%',
  },
  logo: {
    cursor: 'pointer',
  },
  notificationButton: {
    width: 'auto',
    lineHeight: 0,
    padding: 10,
  },
  notificationBadge: {
    height: 15,
    width: 15,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1,
  },
  hidden: {
    visibility: 'hidden',
  },
  divider: {
    color: theme.white,
    fontSize: theme.fontSizes.xlarge,
    fontWeight: theme.fontWeights.thin,
    margin: '0px !important',
  },
  shopIcon: {
    paddingBottom: 1,
  },
}), { name: 'TopBar' })

const TopBar = () => {
  const navigate = useNavigate()
  const classes = useStyles()
  const dispatch = useAppDispatch()

  const notificationIconSize = useRef({ height: 16, width: 16 })
  const userInfo = useAppSelector(state => state.user.info)
  const tenantInfo = useAppSelector(state => state.tenant.info)
  const appsMenuItems = useAppSelector(state => state.apps.menuItems)
  const menuItemsAlreadyFetched = useAppSelector(state => state.apps.menuItemsAlreadyFetched)
  const marketplaceAccess = useAppSelector(state => state.permission.moduleAccess[SUBSCRIPTION_MODULES.MARKETPLACE])
  const isLoading = useAppSelector(state => state.subscription.details?.loading)
  const subscriptions = useAppSelector(state => state.subscription.details?.subscriptions)
  const hasAppRights = checkAccessRights(USER_RIGHTS.APPS.READ, userInfo)
  const calculating = useAppSelector(state => state.document.upload.calculating)
  const pending = useAppSelector(state => state.document.upload.pending)

  const hasActiveMainSubscription = useMemo(() => {
    return subscriptions
      .filter(sub => sub.type !== subscriptionTypes.FREE_2025)
      .filter(sub => !sub.isSubscriptionLocked && !sub.isTrial && !sub.isExpired && sub.typeOfSubscription === SubscriptionTypeTypes.Main).length === 1
  }, [subscriptions])

  const hasPaidSubscription = useMemo(() => {
    if (!subscriptions || subscriptions.length === 0) {
      return false
    }
    return subscriptions.some(sub => sub.isTrial === false)
  }, [subscriptions])

  useEffect(() => {
    if (marketplaceAccess && !menuItemsAlreadyFetched && hasAppRights) {
      dispatch(fetchMenuItems())
    }
  }, [marketplaceAccess, hasAppRights, menuItemsAlreadyFetched])

  const settingsMenuItem = useMemo(() => {
    if (isLoading) {
      return null
    }
    const settingsMenuData = getSettingsMenuItem(userInfo, tenantInfo, true)
    return (<MenuBarItem showOnlyIcon iconSize={{ height: 16, width: 16 }} {...settingsMenuData} />)
  }, [userInfo, tenantInfo, isLoading])
  const marketPlaceMenuItem = useMemo(() => {
    if (isLoading) {
      return null
    }
    const marketPlaceMenuData = getAppsMenuItem(appsMenuItems, true)
    return marketPlaceMenuData && (
      <MenuBarItem showOnlyIcon iconSize={{ height: 16, width: 16 }} {...marketPlaceMenuData} />)
  }, [appsMenuItems, userInfo, isLoading])
  const shopMenuItem = useMemo(() => {
    if (isLoading) { return null }
    const shopMenuData = getMarketplaceMenuItem(userInfo, true)
    return isMeinbuero && shopMenuData && <MenuBarItem iconSize={{ height: 16, width: 16 }} iconClassName={classes.shopIcon} {...shopMenuData} />
  }, [isLoading])

  const navigateToDashboard = (): void => {
    navigate('/dashboard')
  }

  // race conditions with the cello boot function, if we are not hiding it through a class
  const celloButton = !isMeinbuero && (<CelloButton
    iconSize={{ height: 16, width: 16 }}
    styles={{
      root: clsx(classes.notificationButton, (!hasActiveMainSubscription) && classes.hidden),
      badge: classes.notificationBadge,
    }}
  />)
  const uploadButtonRef = useRef()

  const firstStepsMenuItem = useMemo(() => {
    if (isOnboardingIconEnabled(subscriptions, userInfo?.isDemo)) {
      return (
        <MenuBarItem
          showOnlyIcon
          iconSize={{ height: 16, width: 16 }}
          name={MenuItems.FirstSteps}
          title="Erste Schritte"
          url="/firstSteps"
          isIncludedInPlan={true}
          requiredSub={null}
          submenuItems={[]}
          badgeLabel={''}
        />
      )
    }
    return null
  }, [isMeinbuero, isLoading, hasPaidSubscription, userInfo?.isDemo, subscriptions])

  return (
    <div className={classes.root}>
      <div className={classes.leftArea}>
        <div className={classes.logoWrapper}>
          <SVGIcon
            className={classes.logo}
            svg={LogoSvg}
            width="120px"
            height="auto"
            onClick={navigateToDashboard}
            id="menuV2HeaderLogo" />
        </div>
      </div>
      <div className={classes.centerArea}>
        <UpgradeNotch />
        <RevenueLimitNotch />
        <TaxConsultantUserSwitchNotch />
        <StopDemoNotch />
      </div>
      <div className={classes.rightArea}>
        {celloButton}
        {shopMenuItem && <>
          {shopMenuItem}
          <div className={classes.divider}>|</div>
        </>}
        {(pending.length || calculating.length)
          ? <div ref={uploadButtonRef}><UploadStatusButton buttonRef={uploadButtonRef} /></div>
          : null
        }
        {firstStepsMenuItem}
        {settingsMenuItem}
        {marketPlaceMenuItem}
        <NotificationButton
          iconSize={notificationIconSize.current}
          hideActiveIcon={true}
          styles={{ root: classes.notificationButton, badge: classes.notificationBadge }}
        />
        <AccountButton useNewMenuComponents popoverOffset={{ vertical: -20, horizontal: 'center' }} />
      </div>
    </div>
  )
}

export default TopBar
